"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ContentSkeleton } from "@/components/site/content-skeleton";
import { Button } from "@/components/ui/button";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";

import { AdminDialog } from "@/components/admin/admin-dialog-layout";
import { HelpTip } from "@/components/ui/help-tip";
import { apiFetch } from "@/lib/api-client";

type PurchaseInfo = {
  name: string;
  kind: "single" | "team";
  price: number;
  contentId: string;
  owned: boolean;
  deductions: { name: string; price: number }[];
  payable: number;
  unlocks: { id: string; name: string }[];
  points: number;
};

const POINTS_HREF = "/member/points/";

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="flex items-center gap-1 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right text-foreground">{children}</dd>
    </div>
  );
}

export function AgentPurchaseDialog({
  agentId,
  open,
  onOpenChange,
  onPurchased,
}: {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchased: () => void;
}) {
  const t = useTranslations("member.aiChat.purchase");
  const locale = useLocale();
  const [busy, setBusy] = useState(false);
  const ui = useTranslations("site.ui");
  const infoQuery = useQuery({
    queryKey: ["member", "ai", "agent-purchase", agentId],
    enabled: open,
    gcTime: 0,
    queryFn: ({ signal }) =>
      apiFetch<PurchaseInfo>(`/api/member/ai/agents/${agentId}/purchase`, { signal }),
  });
  const info = infoQuery.data;
  const short = info ? !info.owned && info.points < info.payable : false;

  async function buy() {
    if (!info) return;

    if (info.owned) {
      onOpenChange(false);
      onPurchased();
      return;
    }

    setBusy(true);

    try {
      await apiFetch("/api/member/content/unlock", {
        method: "POST",
        body: { module: "agent", contentId: info.contentId },
      });
      toast.success(t("purchased"));
      onOpenChange(false);
      onPurchased();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminDialog
      open={open}
      onOpenChange={onOpenChange}
      title={info?.kind === "team" ? t("teamTitle") : t("title")}
      size="sm"
      saving={busy}
      confirm={{
        label: info?.owned ? t("start") : info?.payable === 0 ? t("claim") : t("confirm"),
        disabled: !info || short,
        onClick: () => void buy(),
      }}
    >
      {info ? (
        <dl className="space-y-3 text-sm">
          <Row label={info.kind === "team" ? t("team") : t("agent")}>{info.name}</Row>
          <Row
            label={
              <>
                {t("price")}
                <HelpTip>{info.kind === "team" ? t("teamPriceHint") : t("priceHint")}</HelpTip>
              </>
            }
          >
            <span className={info.kind === "team" ? undefined : "font-medium text-brand"}>
              {t("points", { points: info.price })}
            </span>
          </Row>
          {info.deductions.map((item) => (
            <Row key={item.name} label={t("deduct", { name: item.name })}>
              −{t("points", { points: item.price })}
            </Row>
          ))}
          {info.kind === "team" ? (
            <Row label={t("payable")}>
              <span className="font-medium text-brand">{t("points", { points: info.payable })}</span>
            </Row>
          ) : null}
          {info.unlocks.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("unlocks", {
                names: new Intl.ListFormat(locale, { type: "conjunction" }).format(
                  info.unlocks.map((item) => item.name),
                ),
              })}
            </p>
          ) : null}
          <Row label={t("balance")}>
            {t("points", { points: info.points })}
          </Row>
          {info.owned ? (
            <p className="text-muted-foreground">{t("owned")}</p>
          ) : short ? (
            <p className="text-destructive">
              {t("short", { points: info.payable - info.points })}
              <Link href={POINTS_HREF} className="ml-2 text-brand hover:underline">
                {t("topUp")}
              </Link>
            </p>
          ) : null}
        </dl>
      ) : (
        <div className="text-sm text-muted-foreground">
          {infoQuery.isError ? (
            <div role="alert">{infoQuery.error.message}<Button variant="link" onClick={() => void infoQuery.refetch()}>{ui("retry")}</Button></div>
          ) : (
            <ContentSkeleton variant="form" className="w-full" label={ui("loading")} />
          )}
        </div>
      )}
    </AdminDialog>
  );
}
